import {useCallback, useEffect, useRef, useState} from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {plural} from '@lingui/core/macro'
import {Trans, useLingui} from '@lingui/react/macro'

import {
  playReadingAudio,
  type ReadingAudioHandle,
  stopReadingAudio,
  subscribeReadingPreempted,
} from '#/lib/flashbookAudio'
import {getSlideMaxPoints} from '#/lib/scoring'
import {atoms as a, useTheme} from '#/alf'
import {SlidePlayerRenderer} from '#/components/capsules/play/SlidePlayerRenderer'
import {type SlideAnswerResult} from '#/components/capsules/play/types'
import {useDocumentQuestionPalette} from '#/components/documents/documentQuestionPalette'
import {
  FlashBookPlayPaletteProvider,
  inkOn,
} from '#/components/flashbooks/FlashBookPlayTheme'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type Slide} from '#/types/capsule'
import {
  type FlashBookBlockResult,
  type FlashBookPageResult,
} from '#/types/flashbook'

/**
 * Playing what a page carries: its clip, and its questions.
 *
 * Both live here rather than in the carousel because both are *self-contained
 * about a page*: a transport that plays one URL, and a card that walks one page's
 * questions. The carousel decides when they are on screen and what a finished
 * read means; this file decides how they behave.
 *
 * The questions are rendered by the capsule play engine — the same widgets, the
 * same feedback language, the same scoring — inside a card that is not a book
 * page. That is deliberate: a question written on a PDF page is the same object
 * as a question in a capsule or a FlashBook, and giving it a fourth renderer
 * would make it a fourth kind of question. What the card adds is the only thing
 * the engine does not have: *where you are in the sequence*, and a way past the
 * last one.
 *
 * The card is drawn in the app's own mode, not the player's dark one. It is app
 * furniture — a dialog over a page — and a reader who has chosen light mode is
 * reading a white document; a black card in the middle of it would be the one
 * thing on screen still in another mode. So the card takes its own colours from
 * the theme, and the widgets inside it are handed the app's question palette
 * (`documentQuestionPalette`) so that a question's options, inputs and verdicts
 * land on the right side of the same decision.
 */

/** A clip's clock, as `m:ss`. */
function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  const whole = Math.floor(seconds)
  const mins = Math.floor(whole / 60)
  const secs = whole % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * The page's clip: a control that opens into a transport, on the stage's own
 * furniture.
 *
 * It joins the row of controls the stage already keeps at its top-right — expand,
 * and in the composer remove and the material button — so nothing collides with
 * anything and the reader learns one place to look. Collapsed, it is a circle with
 * a speaker; open, it is a small transport with a clock and a seek bar, because a
 * clip attached to a page is one you want to scrub, not just start.
 *
 * Unlike the questions, a clip plays here as well as full screen. Questions are
 * answered *between* pages and only read one at a time, so they need the room and
 * the sequence that the reader gives them; a clip is fifteen seconds of speech
 * attached to the page in front of you, and sending a reader through a full screen
 * reader to hear it would be charging them a door for nothing.
 *
 * Playback goes through the app's shared reading-audio voice, which ducks the
 * background music while it plays and stops whatever else was speaking. A page's
 * audio is a reading, and the app already has one rule about readings.
 */
export function PageAudioControl({
  audioUrl,
  isPending = false,
  onRequest,
  testID = 'pageAudioControl',
}: {
  /**
   * The clip to play. Absent while the material is still being fetched — the
   * control is on the page from the first paint, on the strength of the post's
   * own page table, and the clip arrives when it arrives.
   */
  audioUrl?: string
  /** The material is on its way, so a press has nothing to open yet. */
  isPending?: boolean
  /** Called on the first press: this is what starts the fetch. */
  onRequest?: () => void
  testID?: string
}) {
  const {t: l} = useLingui()
  /**
   * The pill's fill, the open transport's, and the ink on both.
   *
   * The dark scrim every control over a page wears, in both modes, because the
   * page under it is white paper in both — and every surface that draws this
   * control draws it *on* the page: the card's stage corner, the composer's own
   * preview, and the full screen reader, which keeps it at the top of the page
   * rather than among its own chrome buttons for the same reason the card does.
   */
  const film = 'rgba(0, 0, 0, 0.55)'
  const filmOpen = 'rgba(0, 0, 0, 0.7)'
  const ink = '#fff'
  const [isOpen, setOpen] = useState(false)
  const [isPlaying, setPlaying] = useState(false)
  const [progress, setProgress] = useState({position: 0, duration: 0})
  const [trackWidth, setTrackWidth] = useState(0)
  const handleRef = useRef<ReadingAudioHandle | null>(null)
  const playingRef = useRef(false)

  const stop = useCallback(() => {
    handleRef.current = null
    playingRef.current = false
    setPlaying(false)
    setProgress({position: 0, duration: 0})
    stopReadingAudio()
  }, [])

  /** Anything that takes the speakers from us means we are no longer playing. */
  useEffect(() => subscribeReadingPreempted(() => stop()), [stop])

  /** A clip belongs to its page: leaving the page ends it. */
  useEffect(() => stop, [stop, audioUrl])
  useEffect(() => () => stopReadingAudio(), [])

  /** The clock. Polled rather than subscribed, which is how the handle reports. */
  useEffect(() => {
    if (!isPlaying) return
    const id = setInterval(() => {
      const handle = handleRef.current
      if (!handle) return
      if (!handle.isPlaying()) {
        playingRef.current = false
        setPlaying(false)
      }
      setProgress(handle.getProgress())
    }, 250)
    return () => clearInterval(id)
  }, [isPlaying])

  const start = useCallback(() => {
    if (!audioUrl) return
    if (playingRef.current) {
      stop()
      return
    }
    const handle = playReadingAudio(audioUrl, {
      onEnded: () => {
        playingRef.current = false
        setPlaying(false)
        setProgress(current => ({...current, position: current.duration}))
      },
      onError: () => stop(),
    })
    handleRef.current = handle
    playingRef.current = true
    setPlaying(true)
    setProgress(handle.getProgress())
  }, [audioUrl, stop])

  /*
   * The press came first, the clip second.
   *
   * A reader presses play on a control that knows there is audio (the post says
   * so) but does not yet hold the address of it, so the intent is remembered by
   * the open transport and honoured the moment the clip arrives. Nothing plays
   * twice: this only fires while nothing is playing.
   */
  useEffect(() => {
    if (isOpen && audioUrl && !playingRef.current) start()
  }, [isOpen, audioUrl, start])

  const duration = progress.duration
  const fraction = duration > 0 ? Math.min(1, progress.position / duration) : 0

  const seekTo = (x: number) => {
    if (!trackWidth || duration <= 0) return
    const handle = handleRef.current
    if (!handle) return
    const next = Math.max(0, Math.min(1, x / trackWidth))
    handle.seek(next)
    setProgress(current => ({...current, position: next * duration}))
  }

  if (!isOpen) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={l`Listen to this page`}
        accessibilityHint={l`Shows the page's audio player`}
        onPress={() => {
          setOpen(true)
          onRequest?.()
        }}
        testID={testID}
        style={({pressed}) => [
          styles.control,
          {backgroundColor: film, opacity: pressed ? 0.8 : 1},
        ]}>
        {isPending ? (
          <Loader size="xs" fill={ink} />
        ) : (
          <Feather name="volume-2" size={15} color={ink} />
        )}
      </Pressable>
    )
  }

  if (!audioUrl) {
    return (
      <View
        style={[styles.transport, {backgroundColor: filmOpen}]}
        testID={`${testID}-transport`}>
        <Loader size="xs" fill={ink} />
        <Text style={[styles.clock, {color: ink}]}>
          <Trans>Preparing…</Trans>
        </Text>
      </View>
    )
  }

  return (
    <View
      style={[styles.transport, {backgroundColor: filmOpen}]}
      testID={`${testID}-transport`}>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={
          isPlaying ? l`Pause this page's audio` : l`Play this page's audio`
        }
        accessibilityHint=""
        onPress={start}
        style={styles.transportBtn}>
        <Feather name={isPlaying ? 'pause' : 'play'} size={14} color={ink} />
      </TouchableOpacity>
      <Text style={[styles.clock, {color: ink}]}>
        {formatClock(progress.position)}
      </Text>
      <Pressable
        accessibilityRole="adjustable"
        accessibilityLabel={l`Seek within this page's audio`}
        accessibilityHint=""
        onLayout={event => setTrackWidth(event.nativeEvent.layout.width)}
        onPress={event => seekTo(event.nativeEvent.locationX)}
        style={styles.track}>
        <View
          style={[
            styles.trackFill,
            {backgroundColor: ink, width: `${fraction * 100}%`},
          ]}
        />
      </Pressable>
      <Text style={[styles.clock, {color: ink}]}>{formatClock(duration)}</Text>
      <TouchableOpacity
        accessibilityRole="button"
        accessibilityLabel={l`Hide the page's audio player`}
        accessibilityHint=""
        onPress={() => {
          stop()
          setOpen(false)
        }}
        style={styles.transportBtn}>
        <Feather name="x" size={14} color={ink} />
      </TouchableOpacity>
    </View>
  )
}

/**
 * A document that asks something, out in the feed: the one press that plays it.
 *
 * It is about the *document*, not the page in front of the reader, and that is what
 * makes it a call to action rather than a caption: the press starts at the first
 * page that asks anything, through every page that follows
 * (`#/lib/document/quizGate`). A reader who wants to read rather than answer is not
 * held up by it: nothing plays here, and the questions come up between pages only
 * once somebody is reading the document full screen.
 *
 * It opens on that page, not on its question card. A card over a page nobody has
 * looked at yet asks a reader about something they have not read, which is the one
 * thing a question may never do — so the press starts a *reading* that asks, and
 * the questions arrive as the door out of the page that raises them, exactly where
 * they arrive for a reader who simply turned the pages to get here.
 *
 * It is the button and nothing else. The count of what the document asks stands
 * under the title in the reader's own words (see `DocumentQuestionCount`), so the
 * press wears no frame of its own — a pill around a button that is already the end
 * of a line was a card drawn inside a card, and the line it ended was where the
 * reader's eye already was.
 *
 * A reader who has already worked some of the pages is offered *Carry on* instead
 * of *Play*, and taken back to where they stopped. Nothing about the press moves:
 * same place, same size, same one tap — what changes is that it no longer asks
 * them to do the work they have already done (`#/lib/document/report`).
 */
export function DocumentPlayCallout({
  count,
  resumeFrom,
  counts,
  onPlay,
}: {
  /** How many questions the whole document holds, for what the press promises. */
  count: number
  /**
   * The page this reader's own read stopped before, when it did.
   *
   * Present only for a read that has something left in it; the caller decides —
   * see `documentResume` — so this component never has to ask whether a page has
   * questions on it.
   */
  resumeFrom?: number
  /**
   * How much of the read is done, in both the numbers a reader is told.
   *
   * Said rather than counted here, because only the caller can: the answers are in a
   * submission and the pages read are in the reader's own record, and a press that
   * recomputed them would be a press that disagreed with the caption beside it.
   */
  counts?: {pagesSeen: number; answered: number}
  /** Plays the document: at the page to carry on from, or its first question page. */
  onPlay: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const isResuming = resumeFrom !== undefined
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={
        isResuming
          ? l`Carry on with this document's questions`
          : l`Play this document's questions`
      }
      accessibilityHint={
        resumeFrom !== undefined
          ? counts
            ? l`Continues from page ${resumeFrom}, where you stopped: ${counts.answered} of ${count} questions answered over ${counts.pagesSeen} pages read`
            : l`Continues from page ${resumeFrom}, where your last read stopped`
          : plural(count, {
              one: 'One question, read and answered in the full screen reader',
              other: '# questions, read and answered in the full screen reader',
            })
      }
      onPress={onPlay}
      testID="documentPlayCallout"
      style={[styles.playAction, {backgroundColor: t.palette.primary_500}]}>
      <Feather name="play" size={13} color="#fff" />
      <Text style={[a.text_xs, a.font_bold, styles.playActionTxt]}>
        {isResuming ? <Trans>Carry on</Trans> : <Trans>Play</Trans>}
      </Text>
    </TouchableOpacity>
  )
}

/**
 * What the document asks, said in words, under its title.
 *
 * The count used to live inside the press that plays it, which made one small bar
 * carry both the offer and the number of it. Here it is a caption instead: the
 * document's own line, in the colour the app already uses for a post's handle —
 * something worth saying about what is above it, rather than something to press.
 *
 * A reader with a read to carry on is told *where they left off* instead of how
 * many questions the document holds, and the swap is the point: what a document
 * asks is what somebody deciding whether to start wants to know, and somebody who
 * has already started wants to know where they stopped. Their standing among the
 * other readers is not lost by the swap — it is on the card's own strip, from a
 * reader's place to the leaderboard behind it (`DocumentPlayerStrip`).
 *
 * They are told *three* things, in one caption, because a read has three parts and the
 * two numbers are worthless apart: where they stopped, how much of the document they
 * have read, and how much of its asking they have answered. Somebody told only the page
 * does not know how far in they are; somebody told only the questions does not know how
 * much reading is left, and most of a document asks nothing at all — the pages read is
 * the number that says a reader is nine pages into a twenty-eight page deck with every
 * question behind them, which is exactly the reader a carry-on is for.
 */
export function DocumentQuestionCount({
  count,
  resumeFrom,
  counts,
}: {
  count: number
  /** The page a read carries on from, when the reader has one to carry on. */
  resumeFrom?: number
  /** How much of the read is done: the pages seen, and the questions answered. */
  counts?: {pagesSeen: number; answered: number}
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  /**
   * The caption, in the parts that are always true of a read and the ones that are
   * only there when it has gone far enough to have them.
   *
   * Built as parts rather than as a sentence with holes so that neither number is
   * printed as a zero: a reader who has answered nothing is not told "0 of 2", and a
   * document that asks nothing does not say so — the join is what the caption looks
   * like either way.
   */
  const resumeLine =
    resumeFrom === undefined
      ? null
      : [
          l`Carry on from page ${resumeFrom}`,
          ...(counts
            ? [
                plural(counts.pagesSeen, {
                  one: '# page read',
                  other: '# pages read',
                }),
                counts.answered > 0 && count > 0
                  ? l`${counts.answered} of ${count} answered`
                  : '',
              ]
            : []),
        ]
          .filter(Boolean)
          .join(' · ')
  return (
    <View
      style={[a.flex_row, a.align_center, a.gap_2xs]}
      testID="documentQuestionCount">
      <Feather
        name={resumeFrom !== undefined ? 'play-circle' : 'help-circle'}
        size={11}
        color={t.atoms.text_contrast_medium.color}
      />
      <Text style={[a.text_xs, t.atoms.text_contrast_medium]} numberOfLines={1}>
        {resumeLine ??
          plural(count, {
            one: '# question in this document',
            other: '# questions in this document',
          })}
      </Text>
    </View>
  )
}

/**
 * The card's place while its questions are on their way.
 *
 * A reader who presses play on a page that asks something has asked for the
 * questions; the fetch behind them is the app's own work, not theirs, so the card
 * appears immediately and says so rather than leaving the press unanswered. Shared
 * by the two surfaces that open the card — the carousel's author preview and the
 * reader's full screen — so "waiting for questions" looks the same in both.
 */
export function MaterialLoadingCard() {
  const t = useTheme()
  const {t: l} = useLingui()
  return (
    <View style={styles.cardScrim} testID="pageMaterialLoading">
      <View
        style={[
          styles.card,
          styles.loadingCard,
          {
            backgroundColor: t.atoms.bg.backgroundColor,
            borderWidth: 1,
            borderColor: t.atoms.border_contrast_low.borderColor,
          },
        ]}>
        <Loader size="sm" fill={t.atoms.text_contrast_medium.color} />
        <Text style={[styles.previewTag, t.atoms.text_contrast_medium]}>
          {l`Getting this page's questions…`}
        </Text>
      </View>
    </View>
  )
}

/**
 * The card's place when its questions could not be read at all.
 *
 * The page's card is the door out of a page that asks something, so it stays up
 * when the read behind it failed — but what it was going to say cannot be said,
 * and a spinner over a request that has already ended is the one thing on screen
 * that is worse than the truth. So the same card says what happened, in the
 * reader's own terms: the questions live apart from the post, the read did not
 * get through, and nothing is lost by asking again — the record is written once
 * with its post and never edited, so what is behind it is still there.
 *
 * The press is *deliberate*: the reader's asks are counted and single
 * (`#/lib/document/useMaterialRequest`), and only the reader's hand may make a
 * new one. Nothing here retries on its own, and nothing here retries on a render.
 */
export function MaterialFailedCard({onRetry}: {onRetry?: () => void}) {
  const t = useTheme()
  const {t: l} = useLingui()
  return (
    <View style={styles.cardScrim} testID="pageMaterialFailed">
      <View
        style={[
          styles.card,
          styles.failedCard,
          {
            backgroundColor: t.atoms.bg.backgroundColor,
            borderWidth: 1,
            borderColor: t.atoms.border_contrast_low.borderColor,
          },
        ]}>
        <Feather
          name="cloud-off"
          size={14}
          color={t.atoms.text_contrast_medium.color}
        />
        <Text
          style={[styles.previewTag, t.atoms.text_contrast_medium]}
          testID="pageMaterialFailedCaption">
          {l`Could not read this page's questions`}
        </Text>
        <Text style={[styles.previewTag, t.atoms.text_contrast_medium]}>
          {l`They are stored beside the post, and nothing was lost — the reading did not get through.`}
        </Text>
        {onRetry && (
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Try reading this page's questions again`}
            accessibilityHint={l`Reads this post again, then the questions it names`}
            onPress={onRetry}
            testID="pageMaterialRetry"
            style={[
              styles.retryAction,
              {backgroundColor: t.palette.primary_500},
            ]}>
            <Feather name="refresh-cw" size={12} color="#fff" />
            <Text style={[a.text_xs, a.font_bold, styles.playActionTxt]}>
              <Trans>Try again</Trans>
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  )
}

/**
 * The same failure, on the card outside — a pill where the play press stands.
 *
 * A reader who never opened the reader met the material once already: the fetch
 * starts when this card is *played*, and a document that failed to read then must
 * not go on offering a press that opens onto nothing. It says the same thing in
 * one line, in the same place the offer was, and it asks again on the same
 * deliberate press.
 */
export function DocumentMaterialRetry({onRetry}: {onRetry: () => void}) {
  const t = useTheme()
  const {t: l} = useLingui()
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={l`Could not load this document's questions. Try again`}
      accessibilityHint={l`Reads this post again, then the questions it names`}
      onPress={onRetry}
      testID="documentMaterialRetry"
      style={[
        styles.playAction,
        {
          backgroundColor: 'transparent',
          borderWidth: 1,
          borderColor: t.atoms.border_contrast_low.borderColor,
        },
      ]}>
      <Feather
        name="refresh-cw"
        size={12}
        color={t.atoms.text_contrast_medium.color}
      />
      <Text
        style={[
          a.text_xs,
          a.font_bold,
          styles.playActionTxt,
          t.atoms.text_contrast_medium,
        ]}>
        {l`Couldn't load the questions`}
      </Text>
    </TouchableOpacity>
  )
}

/**
 * One page's questions, one at a time.
 *
 * The engine renders a question and its verdict; this steps between them. The
 * order is the author's, the step chip says how far in the reader is, and the
 * last button says *Finish* rather than *Next* — the sequence has an end, and a
 * control that promised another question would be lying about it.
 *
 * Answers are recorded per question, and a retry replaces the answer it retries,
 * so a reader who gets one right on the second try is recorded as having got it
 * right on the page they are answering — the same rule the book player applies.
 */
export function PageQuestionsCard({
  page,
  questions,
  accent,
  isPreview = false,
  note,
  onFinish,
  onDismiss,
}: {
  /** 1-based page the card is over, which is what identifies it in a play. */
  page: number
  questions: Slide[]
  accent: string
  /** Composer preview: the reader is the author, and nothing is recorded. */
  isPreview?: boolean
  /**
   * What finishing this card does, said in the reader's own terms.
   *
   * A page's questions are the door to the page after it, and a door reads better
   * with a sign on it: *answer to carry on to page 4* is the difference between a
   * card the reader experiences as the next thing and one they experience as an
   * interruption. Omitted where the card is not a door (the author's preview).
   */
  note?: string
  onFinish: (result: {
    pageResults: FlashBookPageResult
    score: number
    maxPoints: number
  }) => void
  onDismiss: () => void
}) {
  const t = useTheme()
  const questionsPalette = useDocumentQuestionPalette()
  const {t: l} = useLingui()
  /** The ink of the card's own action, which is the accent's, not white's. */
  const actionInk = inkOn(accent)
  const [index, setIndex] = useState(0)
  const [results, setResults] = useState<Record<string, FlashBookBlockResult>>(
    {},
  )
  const startedAt = useRef<number>(Date.now())

  const question = questions[index]
  const isLast = index === questions.length - 1
  const answered = !!question && !!results[question.id]

  useEffect(() => {
    startedAt.current = Date.now()
  }, [index])

  const onAnswer = (result: SlideAnswerResult) => {
    if (!question) return
    setResults(current => ({
      ...current,
      [question.id]: {
        blockId: question.id,
        type: question.type,
        isCorrect: result.isCorrect,
        timeSpentMs: Date.now() - startedAt.current,
        userAnswer: result.userAnswer,
        points: result.points,
      },
    }))
  }

  const finish = () => {
    const blocks = questions
      .map(item => results[item.id])
      .filter((block): block is FlashBookBlockResult => !!block)
    const score = blocks.reduce((sum, block) => sum + (block.points ?? 0), 0)
    const maxPoints = questions.reduce(
      (sum, item) => sum + getSlideMaxPoints(item),
      0,
    )
    onFinish({
      pageResults: {
        // A page of a PDF has no record of its own, so the page *number* is its
        // identity — and the only thing a per-page report can group by.
        pageId: String(page),
        timeSpentMs: blocks.reduce((sum, block) => sum + block.timeSpentMs, 0),
        blocks,
      },
      score,
      maxPoints,
    })
  }

  if (!question) return null

  return (
    <View style={styles.cardScrim} testID="pageQuestionsCard">
      {/* Tapping outside is how you leave a card you did not finish. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={l`Close the questions`}
        accessibilityHint={l`Leaves the questions without answering them all`}
        onPress={onDismiss}
        style={styles.scrimTouch}
      />
      <View
        style={[
          styles.card,
          {
            backgroundColor: t.atoms.bg.backgroundColor,
            // The card floats over a dimmed page, and in the light mode it is
            // white — the one thing a white card cannot do on its own is say
            // where it ends.
            borderWidth: 1,
            borderColor: t.atoms.border_contrast_low.borderColor,
          },
        ]}>
        <View style={styles.cardHead}>
          <View style={[styles.stepChip, {borderColor: accent + '66'}]}>
            <Text style={[styles.stepTxt, {color: accent}]}>
              {index + 1} / {questions.length}
            </Text>
          </View>
          {isPreview && (
            <Text style={[styles.previewTag, t.atoms.text_contrast_medium]}>
              <Trans>Preview — not counted</Trans>
            </Text>
          )}
          <TouchableOpacity
            accessibilityRole="button"
            accessibilityLabel={l`Close the questions`}
            accessibilityHint=""
            onPress={onDismiss}
            style={[styles.closeBtn, t.atoms.bg_contrast_50]}>
            <Feather name="x" size={15} color={t.atoms.text.color} />
          </TouchableOpacity>
        </View>

        {note && (
          <Text
            style={[styles.noteTxt, t.atoms.text_contrast_medium]}
            numberOfLines={1}>
            {note}
          </Text>
        )}

        <ScrollView
          style={styles.cardScroll}
          contentContainerStyle={styles.cardScrollInner}
          showsVerticalScrollIndicator={false}>
          <FlashBookPlayPaletteProvider palette={questionsPalette}>
            <SlidePlayerRenderer
              key={question.id + index}
              slide={question}
              primaryColor={accent}
              isPreview={isPreview}
              onAnswer={onAnswer}
            />
          </FlashBookPlayPaletteProvider>
        </ScrollView>

        <View style={styles.cardFoot}>
          {!isLast ? (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Next question`}
              accessibilityHint={l`Goes to the next question`}
              disabled={!answered}
              onPress={() => setIndex(current => current + 1)}
              testID="pageQuestionsNext"
              style={[
                styles.footBtn,
                {backgroundColor: accent, opacity: answered ? 1 : 0.5},
              ]}>
              <Text style={[styles.footBtnTxt, {color: actionInk}]}>
                <Trans>Next</Trans>
              </Text>
              <Feather name="chevron-right" size={15} color={actionInk} />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={l`Finish these questions`}
              accessibilityHint={l`Ends this page's questions`}
              disabled={!answered}
              onPress={finish}
              testID="pageQuestionsFinish"
              style={[
                styles.footBtn,
                {backgroundColor: accent, opacity: answered ? 1 : 0.5},
              ]}>
              <Text style={[styles.footBtnTxt, {color: actionInk}]}>
                <Trans>Finish</Trans>
              </Text>
              <Feather name="check" size={15} color={actionInk} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  /** Shape only — see `clock`: the film is the control's own. */
  control: {
    width: 32,
    height: 32,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center',
  },
  transport: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 6,
    paddingVertical: 6,
    borderRadius: 99,
    minWidth: 220,
  },
  transportBtn: {
    width: 24,
    height: 24,
    borderRadius: 99,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Shape only: the ink is the caller's, because the film is. */
  clock: {
    fontSize: 10.5,
    fontVariant: ['tabular-nums'],
    minWidth: 28,
    textAlign: 'center',
  },
  track: {
    flex: 1,
    height: 14,
    justifyContent: 'center',
    borderRadius: 99,
  },
  trackFill: {
    position: 'absolute',
    height: 3,
    borderRadius: 99,
  },
  cardScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 60,
  },
  scrimTouch: {position: 'absolute', top: 0, left: 0, right: 0, bottom: 0},
  /**
   * The card's shape only. Its colour is the app's own surface, resolved where
   * it is used — the card follows the app's mode, so a value here would be a
   * dark card in a light app.
   */
  card: {
    width: '100%',
    maxWidth: 460,
    maxHeight: '94%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  stepChip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
    borderWidth: 1,
  },
  stepTxt: {fontSize: 11, fontWeight: '800'},
  noteTxt: {
    fontSize: 10.5,
    paddingHorizontal: 10,
    paddingBottom: 6,
  },
  previewTag: {fontSize: 10.5},
  closeBtn: {
    marginLeft: 'auto',
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    maxWidth: 260,
  },
  /**
   * The failure's card, stacked rather than a row.
   *
   * This one has three things to say and a press at the end of them — what the
   * reader was waiting for, why it is not there, and what asking again will do — so
   * it reads as a small panel rather than as the one-line the spinner was. Still a
   * narrow column: it is drawn over a page, and a page's questions are the point.
   */
  failedCard: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 14,
    maxWidth: 300,
  },
  retryAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    marginTop: 2,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 9,
  },
  /**
   * The press that plays the document, at the end of the title line.
   *
   * The one control in the card that is *asking* for something, so it is the one
   * that is largest: it is sized to be pressed on a phone, not to sit quietly in a
   * row of counts.
   */
  playAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 10,
  },
  playActionTxt: {color: '#fff'},
  cardScroll: {flexGrow: 0},
  cardScrollInner: {paddingHorizontal: 12, paddingBottom: 10},
  cardFoot: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 12,
    paddingBottom: 12,
    paddingTop: 4,
  },
  footBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 12,
  },
  footBtnTxt: {fontSize: 13, fontWeight: '800'},
})
